import {Component, inject, Signal} from '@angular/core';
import {CommonModule} from '@angular/common';

import {rxResource} from '@angular/core/rxjs-interop';
import {map, NEVER} from 'rxjs';

import {CubesManagerModule} from '@studio2wx/cubes-manager';
import {L12mBarChartComponent} from '@selpia/data-batch/ui';
import {Measure} from '@selpia/shared/models';
import {AppConfigFacade} from '@selpia/app-config/data-access';
import {OptionsFacade} from '@selpia/options/data-access';
import {PerspectiveFacade} from '@selpia/perspective/data-access';
import {DataBatchService, DTOsConverter} from '@selpia/data-batch/data-access';
import {InteractionMessageComponent, SvgSpriteIcon} from '@selpia/shared/ui';
import {NavigationService} from '@selpia/data-batch/history-access';

@Component({
  selector: 'lib-feature-trends',
  imports: [CommonModule, CubesManagerModule, L12mBarChartComponent, InteractionMessageComponent],
  templateUrl: './feature-trends.component.html',
  styleUrl: './feature-trends.component.scss',
})
export class FeatureTrendsComponent {
  readonly #appConfigFacade = inject(AppConfigFacade);
  readonly #perspectiveFacade = inject(PerspectiveFacade);
  readonly #optionsFacade = inject(OptionsFacade);
  readonly #dataBatchService = inject(DataBatchService);

  readonly #navigationService = inject(NavigationService);

  protected readonly focusMeasures: Signal<Measure[]> = this.#appConfigFacade.focusMeasures;
  protected readonly periodCode: Signal<string | undefined> = this.#optionsFacade.focusPeriod;
  protected readonly focusMeasure: Signal<Measure | undefined> = this.#optionsFacade.focusMeasure;
  protected readonly date: Signal<Date | undefined> = this.#perspectiveFacade.date;

  readonly #perspective = this.#perspectiveFacade.perspective;

  readonly #dataChunkResource = rxResource({
    request: () => this.#perspective(),
    loader: ({request: perspective}) => {
      if (!perspective) {
        return NEVER;
      }

      const {focusDimensionLevel, filterDimensions} = perspective;
      return this.#dataBatchService.loadDataBatch(
        focusDimensionLevel.name,
        DTOsConverter.buildFilters(filterDimensions, true),
      )
        .pipe(map(dataBatch => DTOsConverter.dtoDataBatch(dataBatch.dataChunks)[0]));
    },
  });

  protected readonly isLoading = this.#dataChunkResource.isLoading;

  protected readonly dataChunk = this.#dataChunkResource.value;

  onChangeFocusMeasure(measure: Measure) {
    this.#navigationService.setFocusMeasure(measure);
  }

  protected readonly SvgSpriteIcon = SvgSpriteIcon;
}
